import { apiRequest, handleCall } from 'syft-acp-core/api/call'
import { queryString } from 'syft-acp-core/api/params'
import type { ApiResponse } from 'syft-acp-core/api/types'

import type { ConfiguredUpliftAction } from './actions'
import { normaliseAmount, scopeKeyOf } from './helpers'
import type { ConfiguredUpliftEntity, WireConfiguredUplift } from './types'

/**
 * Its own endpoint rather than a field on the preferences response: carrying it there would turn
 * that response from an array into an object and break every existing read until both
 * repositories shipped together.
 *
 * flex-core always answers 200 with a body. Where the feature is not live it answers `none`, the
 * same as a live scope with nothing set, and the screen shows no amount for either. An empty 204
 * would fail here: `handleCall` parses every success body as JSON.
 */
export const fetchConfiguredUplift = ({ payload }: ConfiguredUpliftAction) =>
  new Promise<ApiResponse<ConfiguredUpliftEntity>>((resolve, reject) => {
    const { employer_id: employerId, ...reqArgs } = payload
    const req = apiRequest({
      path: `/admin/employers/${employerId}/configured_uplift${queryString(reqArgs)}`,
    })
    const scope_key = scopeKeyOf(payload)
    // The response carries no id, so stamp the scope it answered for, and coerce the amount off
    // its wire form. flex-core never sends a null body, but the generated reducer reads the id off
    // whatever it is given and would throw on null, so a null body is stored as `none` instead:
    // the same answer as nothing set, and the card stays hidden.
    const withScope = (result: ApiResponse<WireConfiguredUplift | null>) =>
      resolve({
        ...result,
        payload: result.payload
          ? { ...result.payload, amount: normaliseAmount(result.payload.amount), scope_key }
          : { scope_key, amount: null, source: 'none' },
      })

    handleCall(req, withScope, reject, false, payload)
  })
